/**
 * @module supabase
 *
 * Supabase client for the browser (frontend).
 *
 * ┌─────────────────────────────────────────────────────────────────┐
 * │ ⚠️  SECURITY RULE: NEVER use SUPABASE_SERVICE_ROLE_KEY here.   │
 * │                                                                │
 * │  This file is bundled into the browser. The service_role key   │
 * │  bypasses ALL Row Level Security (RLS) and must ONLY be used   │
 * │  server-side in Edge Functions.                                │
 * │                                                                │
 * │  Allowed key: VITE_SUPABASE_ANON_KEY (respects RLS)           │
 * │  Forbidden:   SUPABASE_SERVICE_ROLE_KEY (bypasses RLS)         │
 * │                                                                │
 * │  Enforced by: A6 audit: grep for SERVICE_ROLE in src/        │
 * │  Last audit:  2026-04-26, zero matches ✅                     │
 * └─────────────────────────────────────────────────────────────────┘
 */
import { createClient } from "@supabase/supabase-js";
import type { Database } from "../types/database.types";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage,
  },
});

// ─────────────────────────────────────────────────────────
// MULTI-TENANT ORGANIZATION HELPERS
// ─────────────────────────────────────────────────────────

/**
 * Returns the organization ID from the environment.
 * Each deployed app instance is scoped to a single org via VITE_ORGANIZATION_ID.
 * Falls back gracefully for backward compatibility during migration.
 */
export function getOrganizationId(): string | null {
  return import.meta.env.VITE_ORGANIZATION_ID || null;
}

/**
 * Returns the organization ID or throws if not configured.
 * Use this in contexts where org_id is required (e.g., data inserts).
 */
export function requireOrganizationId(): string {
  const orgId = getOrganizationId();
  if (!orgId) {
    throw new Error(
      "VITE_ORGANIZATION_ID is not configured. " +
        "Each deployed app must be scoped to an organization."
    );
  }
  return orgId;
}

/** Organization membership role type */
export type OrgRole = "super_admin" | "admin" | "member" | "viewer";

/** Organization membership record */
export interface OrgMembership {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrgRole;
}

/** Organization details */
export interface OrgDetails {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  settings: Record<string, unknown> | null;
}

/**
 * Fetches the user's membership record for the configured organization.
 * Returns null if the user is not a member.
 */
export async function getOrgMembership(
  userId: string
): Promise<OrgMembership | null> {
  const orgId = getOrganizationId();
  if (!orgId) return null;

  const { data, error } = await supabase
    .from("organization_members")
    .select("id, organization_id, user_id, role")
    .eq("organization_id", orgId)
    .eq("user_id", userId)
    .single();

  if (error || !data) return null;
  return data as OrgMembership;
}

/**
 * Fetches the organization details for the configured org.
 */
export async function getOrgDetails(): Promise<OrgDetails | null> {
  const orgId = getOrganizationId();
  if (!orgId) return null;

  const { data, error } = await supabase
    .from("organizations")
    .select("id, name, slug, logo_url, settings")
    .eq("id", orgId)
    .single();

  if (error || !data) return null;
  return data as OrgDetails;
}

/**
 * Checks if the user is an admin or super admin of the configured organization.
 */
export function isOrgAdmin(membership: OrgMembership | null): boolean {
  return membership?.role === "admin" || membership?.role === "super_admin";
}

/**
 * Checks if the user is the super admin of the configured organization.
 */
export function isOrgSuperAdmin(membership: OrgMembership | null): boolean {
  return membership?.role === "super_admin";
}

/**
 * Checks if the user has the platform-level admin flag.
 * Platform admins are developers who manage all organizations.
 */
export function isPlatformAdmin(
  profile: { is_platform_admin?: boolean } | null
): boolean {
  return profile?.is_platform_admin === true;
}

/**
 * Determines if the user can manage the team (invite, change roles, remove).
 * Currently restricted to platform admins only (developer-managed SaaS).
 */
export function canManageTeam(
  profile: { is_platform_admin?: boolean } | null
): boolean {
  return isPlatformAdmin(profile);
}

// ─────────────────────────────────────────────────────────
// LEGACY HELPERS (kept for backward compatibility)
// ─────────────────────────────────────────────────────────

/**
 * @deprecated Use org membership check instead.
 * Checks if the user has the legacy `claims_access` flag on their profile.
 * Kept during migration period for backward compatibility.
 */
export async function hasClaimsAccess(): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return false;

  // First check org membership (new system)
  const orgId = getOrganizationId();
  if (orgId) {
    const membership = await getOrgMembership(user.id);
    return membership !== null;
  }

  // Fallback to legacy profile check
  const { data: profile } = await supabase
    .from("profiles")
    .select("claims_access")
    .eq("id", user.id)
    .single();

  return profile?.claims_access ?? false;
}

// Helper to get user profile
export async function getUserProfile() {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: profile } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", user.id)
    .single();

  return profile;
}
